import { Controller } from '@hotwired/stimulus'
import { readToolbarContext, buildToolbarUrl, startObserving } from '../../services'

type TurboFrameRequestEndEvent = Event & {
  detail?: { fetchRequest?: { response?: Response }; response?: Response }
}

export default class extends Controller {
  static targets = ['loader']

  declare readonly loaderTarget: HTMLElement

  private frameEl: HTMLFrameElement | null = null
  private previousCtxValue: string | undefined = undefined
  private previousRevision: string | undefined = undefined
  private boundOnLoad!: () => void
  private boundOnRequestEnd!: (event: Event) => void
  private observer: { disconnect(): void; reobserve(): void } | null = null

  get el(): HTMLElement {
    return this.element as HTMLElement
  }

  connect() {
    this.frameEl = this.element.querySelector('#project-toolbar')
    const initialCtx = this.el.dataset.initialToolbarContext
    this.previousCtxValue = initialCtx || undefined

    this.boundOnLoad = this.onLoad.bind(this)
    this.boundOnRequestEnd = this.onRequestEnd.bind(this)

    this.frameEl?.addEventListener('turbo:frame-load', this.boundOnLoad)
    this.frameEl?.addEventListener('turbo:frame-request-end', this.boundOnRequestEnd)

    this.observer = startObserving(() => this.handleMutation())
  }

  disconnect() {
    this.frameEl?.removeEventListener('turbo:frame-load', this.boundOnLoad)
    this.frameEl?.removeEventListener('turbo:frame-request-end', this.boundOnRequestEnd)
    this.observer?.disconnect()
  }

  handleMutation() {
    this.observer?.reobserve()
    const ctx = readToolbarContext()
    if (!ctx?.project_id) {
      this.previousCtxValue = undefined
      this.previousRevision = undefined
      return this.hide()
    }
    this.reload()
  }

  reload() {
    const ctx = readToolbarContext()
    if (!ctx?.project_id) {
      this.previousCtxValue = undefined
      this.previousRevision = undefined
      return this.hide()
    }
    if (!this.frameEl) return this.hide()

    const liveCtx = document.getElementById('toolbar-context')
    const valueChanged = this.previousCtxValue !== liveCtx?.dataset.value
    const revisionChanged = this.previousRevision !== liveCtx?.dataset.revision

    if (!valueChanged && !revisionChanged) return

    this.showLoader()

    this.frameEl.src = buildToolbarUrl(ctx, liveCtx)
    this.el.classList.remove('hide')
    this.previousCtxValue = liveCtx?.dataset.value
    this.previousRevision = liveCtx?.dataset.revision
  }

  hide() {
    this.hideLoader()
    this.frameEl?.removeAttribute('src')
    if (this.frameEl) this.frameEl.innerHTML = ''
    this.el.classList.add('hide')
  }

  onLoad() {
    this.hideLoader()
    this.saveHeight()
    const liveCtx = document.getElementById('toolbar-context')
    if (liveCtx?.dataset.value) {
      this.previousCtxValue = liveCtx.dataset.value
    }
    this.previousRevision = liveCtx?.dataset.revision
    if (this.frameEl && !this.frameEl.innerHTML.trim()) {
      this.hide()
    }
  }

  onRequestEnd(event: Event) {
    const response = (event as TurboFrameRequestEndEvent).detail?.fetchRequest?.response
      || (event as TurboFrameRequestEndEvent).detail?.response
    if (!response || response.status === 204 || response.status >= 400) {
      this.hideLoader()
      this.hide()
    }
  }

  showLoader() {
    const loader = this.loaderTarget
    if (!loader) return

    this.el.style.setProperty(
      '--toolbar-height',
      this.el.dataset.savedHeight || '130px'
    )
    this.el.classList.add('project-toolbar__container--loading')
    loader.classList.remove('hide')
  }

  hideLoader() {
    this.loaderTarget?.classList.add('hide')
    this.el.classList.remove('project-toolbar__container--loading')
    this.el.style.removeProperty('--toolbar-height')
  }

  saveHeight() {
    const height = this.el.offsetHeight
    if (height) {
      this.el.dataset.savedHeight = String(height)
    }
  }
}
